import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import styles from './index.module.css';
import {
    Add20Regular,
    ArrowImport20Regular,
    ArrowSort24Regular,
    ChevronDown16Regular,
    Delete20Regular,
    LockClosed20Regular,
    MoreVertical20Regular,
    NoteAdd24Regular,
    Share20Regular,
    TextEditStyle20Regular,
    Warning20Regular,
} from '@fluentui/react-icons';
import { Menu, MenuTrigger, MenuPopover, MenuList, MenuItem, AvatarGroup, AvatarGroupItem, AvatarGroupPopover } from '@fluentui/react-components';
import JournalEditor from './JournalEditor';
import ShareSectionModal from './ShareSectionModal';
import RewriteAIModal from './RewriteAIModal';
import MoveEntryModal from './MoveEntryModal';
import { useDispatch, useSelector } from 'react-redux';
import { GetNotes, PostNotesPage, DeleteNotePage, PostNote, DeleteNote, PatchNote, PatchNotesPage, MoveNote } from '../../redux/dashboardSlice';
import DoubleConfirmationModal from '../CommonComponents/DoubleConfirmationModal/DoubleConfirmationModal';
import { toast } from 'react-toastify';
import { ObservationFormSidebar } from '../Observations';
import { TaskFormSidebar } from '../TasksInterface';
import { PostInformationRequestSidebar } from '../IRL';
import { getInitials } from '../../utils/commonUtils';
import SafeHTML from '../SafeHTML';
const Journal = () => {
    const dispatch = useDispatch();
    const notesState = useSelector(state => state.dashboard?.notes || []);
    const { user } = useSelector(state => state.user);
    const { projectUsersAll } = useSelector(state => state.team);
    const [selectedSectionId, setSelectedSectionId] = useState(null);
    const [isAddingEntry, setIsAddingEntry] = useState(false);

    // const [draftBody] = useState('');
    const [initialEditorData, setInitialEditorData] = useState(null);
    const [isShareModalOpen, setIsShareModalOpen] = useState(false);
    const [selectedSectionForShare, setSelectedSectionForShare] = useState(null);
    const [isRewriteAIModalOpen, setIsRewriteAIModalOpen] = useState(false);
    const [selectedNoteForRewrite, setSelectedNoteForRewrite] = useState(null);
    const [aiMode, setAiMode] = useState('rewrite');
    const [isMoveEntryModalOpen, setIsMoveEntryModalOpen] = useState(false);
    const [expandedNoteId, setExpandedNoteId] = useState(null);
    const [editingNote, setEditingNote] = useState(null);
    const [draftContent, setDraftContent] = useState(null);
    const debounceTimeoutRef = useRef(null);
    const editorApiRef = React.useRef(null);
    const [autoSavedNoteId, setAutoSavedNoteId] = useState(null);
    const isCreatingNoteRef = useRef(false);

    const latestDraftContentRef = useRef(null);
    const isAddingEntryRef = useRef(false);
    const selectedSectionIdRef = useRef(null);
    const editingNoteRef = useRef(null);
    const autoSavedNoteIdRef = useRef(null);
    const sectionMenuDeleteRef = useRef(null);
    const entryMenuDeleteRef = useRef(null);
    const [selectedSectionForDelete, setSelectedSectionForDelete] = useState(null);
    const [selectedEntryForDelete, setSelectedEntryForDelete] = useState(null);
    const sectionMakePrivateRef = useRef(null);
    const [selectedSectionForMakePrivate, setSelectedSectionForMakePrivate] = useState(null);
    const [editingSectionName, setEditingSectionName] = useState(null);
    const [editingSectionValue, setEditingSectionValue] = useState('');
    const [isSectionsCollapsed, setIsSectionsCollapsed] = useState(false);
    const [sortOrder, setSortOrder] = useState('desc'); // 'desc' = newest first, 'asc' = oldest first

    const [showObsSidebar, setShowObsSidebar] = React.useState(false);
    const [showTaskSidebar, setShowTaskSidebar] = React.useState(false);
    const [showIRLSidebar, setShowIRLSidebar] = React.useState(false);
    const [rewrittenContentForSidebar, setRewrittenContentForSidebar] = React.useState(null);

    // Helper function to get HTML from note (Quill works with HTML directly)
    const getNoteHtml = note => {
        return note?.notes || '';
    };

    const htmlToPlainText = html => {
        const container = document.createElement('div');
        container.innerHTML = html || '';
        return container.textContent || container.innerText || '';
    };

    useEffect(() => {
        dispatch(GetNotes());
    }, [dispatch]);

    // Select first section by default when data arrives
    useEffect(() => {
        if (!selectedSectionId && notesState && notesState.length > 0) {
            setSelectedSectionId(notesState[0].id);
        }
    }, [notesState, selectedSectionId]);
    const sections = useMemo(() => {
        return (notesState || []).map(n => {
            // Determine tag based on sharing status
            let tag = null;

            // Project default sections are public for all users
            if (n?.is_project_default) {
                tag = { text: 'Public', color: '#DA0C81' };
            }
            // Check if this is a notepad shared with me (I'm not the owner)
            else if (n?.is_shared) {
                // Someone else shared this with me
                tag = { text: n?.owner_name, color: '#192BC2' };
            }
            // Check if I own this notepad and have shared it with others
            else if (n?.shared_with && n.shared_with.length > 0) {
                // I shared this with others
                tag = { text: 'Shared', color: '#192BC2' };
            }

            return {
                id: n.id,
                name: n.name,
                tag: tag,
                isActive: n.id === selectedSectionId,
                shared_with: n.shared_with,
                created_by: n.created_by,
                is_project_default: n.is_project_default,
                shared_with_names: n.shared_with_names || [],
            };
        });
    }, [notesState, selectedSectionId, user?.id]);

    const selectedSection = useMemo(() => (notesState || []).find(s => s.id === selectedSectionId) || null, [notesState, selectedSectionId]);

    // Sort notes based on sortOrder (newest first by default)
    const sortedNotes = useMemo(() => {
        const notes = selectedSection?.notes || [];
        return [...notes].sort((a, b) => {
            const dateA = new Date(a.created);
            const dateB = new Date(b.created);
            return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
        });
    }, [selectedSection?.notes, sortOrder]);

    // Auto-save functionality
    const autoSave = useCallback(async () => {
        if (!isAddingEntryRef.current || !selectedSectionIdRef.current) return;

        // Get the latest HTML content from the Quill editor
        const html = (await editorApiRef.current?.save?.()) || '';

        // Don't save if content is empty (check for empty HTML tags)
        const textContent = html.replace(/<[^>]*>/g, '').trim();
        if (!textContent) return;

        try {
            // Send all fields, allowing empty values when editing
            const payload = {
                notepad: selectedSectionIdRef.current,
                notes: html,
            };

            if (editingNoteRef.current) {
                // Auto-save existing note (editing mode)
                dispatch(PatchNote({ id: editingNoteRef.current.id, ...payload }));
            } else if (autoSavedNoteIdRef.current) {
                // Auto-save existing auto-created note
                dispatch(PatchNote({ id: autoSavedNoteIdRef.current, ...payload }));
            } else if (!isCreatingNoteRef.current) {
                // Create new note for the first time (only if not already creating)
                isCreatingNoteRef.current = true;
                const result = await dispatch(PostNote(payload));
                if (result.payload?.data?.id) {
                    setAutoSavedNoteId(result.payload.data.id);
                }
                isCreatingNoteRef.current = false;
            }
        } catch (e) {
            console.error('Auto-save failed:', e);
            isCreatingNoteRef.current = false;
        }
    }, [dispatch]);

    // Update refs when state changes

    useEffect(() => {
        latestDraftContentRef.current = draftContent;
    }, [draftContent]);

    useEffect(() => {
        isAddingEntryRef.current = isAddingEntry;
    }, [isAddingEntry]);

    useEffect(() => {
        selectedSectionIdRef.current = selectedSectionId;
    }, [selectedSectionId]);

    useEffect(() => {
        editingNoteRef.current = editingNote;
    }, [editingNote]);

    // Auto-select newly created note
    useEffect(() => {
        if (autoSavedNoteId && !editingNote && selectedSectionId) {
            // Find the newly created note in the state and select it
            const section = notesState.find(s => s.id === selectedSectionId);
            if (section?.notes) {
                const newNote = section.notes.find(n => n.id === autoSavedNoteId);
                if (newNote) {
                    setEditingNote(newNote);
                    const noteHtml = getNoteHtml(newNote);
                    originalNoteContentRef.current = noteHtml;
                    isInitializingEditorRef.current = true;
                    setInitialEditorData(noteHtml);
                    setTimeout(() => {
                        isInitializingEditorRef.current = false;
                    }, 2000);
                }
            }
        }
    }, [autoSavedNoteId, notesState, selectedSectionId, editingNote]);

    useEffect(() => {
        autoSavedNoteIdRef.current = autoSavedNoteId;
    }, [autoSavedNoteId]);

    // Save when component unmounts or when switching sections
    useEffect(() => {
        return () => {
            if (isAddingEntryRef.current) {
                autoSave();
            }
        };
    }, [isAddingEntry, autoSave]);

    // Track if user is actively typing to prevent initialEditorData from resetting editor
    const isUserTypingRef = useRef(false);
    // Track if editor is being initialized with existing content to prevent auto-save
    const isInitializingEditorRef = useRef(false);
    // Track the original note content to prevent saving unchanged content
    const originalNoteContentRef = useRef(null);

    // Debounced onChange handler
    const handleEditorChange = useCallback(
        html => {
            // Skip auto-save if we're initializing the editor with existing content
            if (isInitializingEditorRef.current) {
                return;
            }

            // Mark that user is typing
            isUserTypingRef.current = true;

            // Update draft content immediately for UI
            setDraftContent(html);

            // Clear existing debounce timeout
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
                debounceTimeoutRef.current = null;
            }

            // Set new debounce timeout for auto-save
            debounceTimeoutRef.current = setTimeout(async () => {
                try {
                    // Double-check conditions before saving
                    if (!isAddingEntryRef.current || !selectedSectionIdRef.current) {
                        debounceTimeoutRef.current = null;
                        return;
                    }

                    // Get the latest content from editor
                    const latestHtml = (await editorApiRef.current?.save?.()) || '';

                    // Don't save if content is empty
                    const textContent = latestHtml.replace(/<[^>]*>/g, '').trim();
                    if (!textContent) {
                        debounceTimeoutRef.current = null;
                        return;
                    }

                    // If editing an existing note, check if content has actually changed
                    if (editingNoteRef.current && originalNoteContentRef.current) {
                        const originalText = originalNoteContentRef.current.replace(/<[^>]*>/g, '').trim();
                        const newText = latestHtml.replace(/<[^>]*>/g, '').trim();

                        // Don't save if content hasn't changed (allowing for minor HTML formatting differences)
                        if (originalText === newText) {
                            debounceTimeoutRef.current = null;
                            return;
                        }
                    }

                    // Save to backend
                    const payload = {
                        notepad: selectedSectionIdRef.current,
                        notes: latestHtml,
                    };

                    if (editingNoteRef.current) {
                        // Auto-save existing note (editing mode)
                        await dispatch(PatchNote({ id: editingNoteRef.current.id, ...payload }));
                    } else if (autoSavedNoteIdRef.current) {
                        // Auto-save existing auto-created note
                        await dispatch(PatchNote({ id: autoSavedNoteIdRef.current, ...payload }));
                    } else if (!isCreatingNoteRef.current) {
                        // Create new note for the first time
                        isCreatingNoteRef.current = true;
                        const result = await dispatch(PostNote(payload));
                        if (result.payload?.data?.id) {
                            setAutoSavedNoteId(result.payload.data.id);
                        }
                        isCreatingNoteRef.current = false;
                    }
                } catch (e) {
                    console.error('Auto-save failed:', e);
                    isCreatingNoteRef.current = false;
                } finally {
                    debounceTimeoutRef.current = null;
                    // Mark that user has stopped typing after save completes
                    setTimeout(() => {
                        isUserTypingRef.current = false;
                    }, 500);
                }
            }, 1000); // Save after 1 second of inactivity
        },
        [dispatch, setAutoSavedNoteId],
    );

    // Delete handlers
    const handleDeleteSection = sectionId => {
        dispatch(DeleteNotePage(sectionId));
        setSelectedSectionForDelete(null);
    };

    const handleDeleteEntry = noteId => {
        // Delete the note
        dispatch(DeleteNote({ id: noteId, notepad: selectedSectionId }));
        // Close confirmation modal
        setSelectedEntryForDelete(null);
        // Ensure we return to the list view (no expanded or edit mode)
        setExpandedNoteId(null);
        setIsAddingEntry(false);
        setEditingNote(null);
        setInitialEditorData(null);
        setAutoSavedNoteId(null);
        isCreatingNoteRef.current = false;
        // Clear editor content
        if (editorApiRef.current?.clear) {
            editorApiRef.current.clear();
        }
        setAutoSavedNoteId(null);
        isCreatingNoteRef.current = false;
        if (editorApiRef.current?.clear) {
            try {
                editorApiRef.current.clear();
            } catch (_) {}
        }
    };

    const handleSectionDeleteClick = section => {
        setSelectedSectionForDelete(section);
        sectionMenuDeleteRef.current?.click();
    };

    const handleEntryDeleteClick = entry => {
        // Use the provided entry or the current editing note
        const noteToDelete = entry || editingNote;
        if (!noteToDelete) return;

        setSelectedEntryForDelete(noteToDelete);
        entryMenuDeleteRef.current?.click();
    };

    const handleRenameSection = (sectionId, currentName) => {
        setEditingSectionName(sectionId);
        setEditingSectionValue(currentName);
    };

    const handleSectionNameChange = e => {
        setEditingSectionValue(e.target.value);
    };

    const handleSectionNameKeyPress = e => {
        if (e.key === 'Enter') {
            handleSaveSectionName();
        }
    };

    const handleSaveSectionName = async () => {
        if (editingSectionName && editingSectionValue.trim()) {
            try {
                await dispatch(
                    PatchNotesPage({
                        id: editingSectionName,
                        name: editingSectionValue.trim(),
                    }),
                ).unwrap();

                // Close editing mode on success
                setEditingSectionName(null);
                setEditingSectionValue('');
            } catch (error) {
                console.error('Failed to update section name:', error);
                // Keep editing mode open on error so user can try again
            }
        }
    };

    const handleMakePrivate = async section => {
        try {
            // Clear all shared users by setting shared_with to empty array
            await dispatch(
                PatchNotesPage({
                    id: section.id,
                    shared_with: [],
                }),
            ).unwrap();
        } catch (error) {
            console.error('Failed to make section private:', error);
        }
    };

    const handleMakePrivateClick = section => {
        setSelectedSectionForMakePrivate(section);
        sectionMakePrivateRef.current?.click();
    };

    const handleRewriteWithAI = async (mode = 'rewrite', note = null) => {
        // Prefer the note being edited if in edit mode
        let targetNote = isAddingEntry ? editingNote || note : note;

        // If we're in edit mode, get the latest content from the editor or draftContent
        if (isAddingEntry) {
            let latestHtml = '';

            // Try to get content from editor first
            if (editorApiRef.current) {
                try {
                    latestHtml = (await editorApiRef.current?.save?.()) || '';
                } catch (error) {
                    console.error('Error getting editor content:', error);
                }
            }

            // Fallback to draftContent if editor API didn't return content
            if (!latestHtml && draftContent) {
                latestHtml = draftContent;
            }

            // Update the note with the latest content
            if (latestHtml) {
                if (targetNote) {
                    targetNote = { ...targetNote, notes: latestHtml };
                } else {
                    // If no note exists yet, create a temporary one with the editor content
                    targetNote = { notes: latestHtml };
                }
            }
        }

        if (!targetNote) return;
        setSelectedNoteForRewrite(targetNote);
        setAiMode(mode);
        setIsRewriteAIModalOpen(true);
    };

    const handleMoveToSection = async targetSectionId => {
        if (!targetSectionId || targetSectionId === selectedSectionId) return;

        try {
            // Determine current note id: prefer editing note in edit mode, else expanded
            const currentNoteId = isAddingEntry ? editingNote?.id || autoSavedNoteIdRef.current : expandedNoteId;
            if (!currentNoteId) return;

            await dispatch(
                MoveNote({
                    noteId: currentNoteId,
                    sourceNotepadId: selectedSectionId,
                    targetNotepadId: targetSectionId,
                }),
            );

            // Reset edit/expanded state and switch to target section
            setExpandedNoteId(null);
            if (isAddingEntry) {
                setIsAddingEntry(false);
                setEditingNote(null);
                setInitialEditorData(null);

                setAutoSavedNoteId(null);
                isCreatingNoteRef.current = false;
                if (editorApiRef.current?.clear) editorApiRef.current.clear();
            }
            setSelectedSectionId(targetSectionId);

            toast.success('Note moved successfully');
        } catch (error) {
            console.error('Failed to move note:', error);
            toast.error('Failed to move note');
        }
    };

    const getProjectMemberName = id => {
        return getInitials(projectUsersAll?.find(item => item.value.user_id === id)?.value?.full_name) || '';
    };

    const formatEntryDate = dateString => {
        if (!dateString) return '';
        try {
            const date = new Date(dateString);
            const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
            const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

            const dayName = days[date.getDay()];
            const monthName = months[date.getMonth()];
            const day = date.getDate();
            const year = date.getFullYear();
            const hours = date.getHours().toString().padStart(2, '0');
            const minutes = date.getMinutes().toString().padStart(2, '0');

            return `${dayName}, ${monthName} ${day}, ${year}, ${hours}:${minutes}hrs`;
        } catch (e) {
            return dateString;
        }
    };

    return (
        <div className={styles.journalContainer}>
            <div className="row g-0 h-100">
                {/* Left Sidebar */}
                {!isSectionsCollapsed && (
                    <div className="col-md-3 col-sm-12">
                        <div className={styles.sidebar}>
                            <div className={styles.sidebarHeader}>
                                <div className={styles.journalTitle}>Journal</div>
                            </div>

                            {sections.map((section, index) => (
                                <div
                                    key={section.id || index}
                                    className={`${styles.sectionItem} ${section.isActive ? styles.activeSection : ''}`}
                                    onClick={() => {
                                        // Collapse any open/edited entries when switching sections
                                        if (expandedNoteId) setExpandedNoteId(null);
                                        setEditingNote(null);
                                        if (isAddingEntry) {
                                            setIsAddingEntry(false);
                                            setInitialEditorData(null);

                                            setAutoSavedNoteId(null);
                                            isCreatingNoteRef.current = false;
                                            if (editorApiRef.current?.clear) {
                                                editorApiRef.current.clear();
                                            }
                                        }
                                        setSelectedSectionId(section.id);
                                    }}>
                                    <div className={styles.sectionContent}>
                                        {editingSectionName === section.id ? (
                                            <input
                                                type="text"
                                                value={editingSectionValue}
                                                onChange={handleSectionNameChange}
                                                onKeyPress={handleSectionNameKeyPress}
                                                onBlur={handleSaveSectionName}
                                                className={styles.sectionNameInput}
                                                autoFocus
                                            />
                                        ) : (
                                            <div className={styles.sectionName}>{section.name}</div>
                                        )}
                                        <div className="d-flex align-items-center gap-2">
                                            {section.tag && (
                                                <div className={styles.sectionTag} style={{ backgroundColor: section.tag.color }}>
                                                    {section.tag.text}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    {user?.id === section?.created_by && !section.is_project_default && (
                                        <Menu>
                                            <MenuTrigger disableButtonEnhancement>
                                                <button style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer' }} aria-label="Section actions">
                                                    <MoreVertical20Regular color="var(--Neutral-Gray-6)" />
                                                </button>
                                            </MenuTrigger>
                                            <MenuPopover>
                                                <MenuList>
                                                    <MenuItem onClick={() => handleRenameSection(section.id, section.name)}>
                                                        <TextEditStyle20Regular className="me-1" /> Rename Section
                                                    </MenuItem>

                                                    <MenuItem
                                                        onClick={() => {
                                                            setSelectedSectionForShare(section);
                                                            setIsShareModalOpen(true);
                                                        }}>
                                                        <Share20Regular className="me-1" /> Share Section
                                                    </MenuItem>
                                                    <MenuItem
                                                        disabled={!section.shared_with || section.shared_with.length === 0 || section.is_project_default}
                                                        onClick={() => handleMakePrivateClick(section)}>
                                                        <LockClosed20Regular className="me-1" /> Make Private
                                                    </MenuItem>
                                                    <MenuItem onClick={() => handleSectionDeleteClick(section)}>
                                                        <Delete20Regular className="me-1" /> Delete Section
                                                    </MenuItem>
                                                </MenuList>
                                            </MenuPopover>
                                        </Menu>
                                    )}
                                </div>
                            ))}

                            {!isSectionsCollapsed && (
                                <div
                                    className={styles.addSectionButton}
                                    onClick={() =>
                                        dispatch(PostNotesPage({ name: 'New Section', project: localStorage['project_id'] }))
                                            .unwrap()
                                            .then(res => {
                                                if (res?.data?.id) {
                                                    setSelectedSectionId(res.data.id);
                                                    // Make the new section name editable and focused
                                                    setEditingSectionName(res.data.id);
                                                    setEditingSectionValue('New Section');
                                                }
                                            })
                                            .catch(() => {})
                                    }>
                                    <Add20Regular color="var(--Brand-Secondary)" />
                                    <div className={styles.addText}>Add new section</div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
                <div className="col-md-3 col-sm-12">
                    <div className={styles.mainContent}>
                        <div className={styles.contentHeader}>
                            <div className={styles.contentTitle}>
                                <div
                                    onClick={() => setIsSectionsCollapsed(!isSectionsCollapsed)}
                                    style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                                    title={isSectionsCollapsed ? 'Expand sections' : 'Collapse sections'}>
                                    {isSectionsCollapsed ? (
                                        /* Collapsed - show expand icon (arrow pointing right) */
                                        <ArrowImport20Regular color="var(--Brand-Secondary)" />
                                    ) : (
                                        /* Expanded - show collapse icon (arrow pointing left) */
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="12" viewBox="0 0 16 12" fill="none">
                                            <path
                                                d="M15.5 12C15.2239 12 15 11.7761 15 11.5V0.5C15 0.2239 15.2239 0 15.5 0C15.7761 0 16 0.2239 16 0.5V11.5C16 11.7761 15.7761 12 15.5 12ZM13 6C13 6.27614 12.7761 6.5 12.5 6.5H1.70711L4.8536 9.64645C5.0488 9.84171 5.0488 10.1583 4.8536 10.3535C4.6583 10.5488 4.3417 10.5488 4.1464 10.3535L0.1464 6.35355C0.093894 6.30102 0.0555096 6.23972 0.03125 6.17437C0.0110435 6.12008 0 6.06133 0 6C0 5.93867 0.0110445 5.87992 0.031251 5.82563C0.0555105 5.76027 0.0938931 5.69897 0.1464 5.64645L4.1464 1.6464C4.3417 1.4512 4.6583 1.4512 4.8536 1.6464C5.0488 1.8417 5.0488 2.15829 4.8536 2.35355L1.7071 5.5H12.5C12.7761 5.5 13 5.72386 13 6Z"
                                                fill="#8E2DE2"
                                            />
                                        </svg>
                                    )}
                                </div>
                                {selectedSection?.name || 'Journal'}
                                {/* {selectedSection?.is_project_default ? (
                                            <div className={styles.sectionTag} style={{ backgroundColor: '#DA0C81' }}>
                                                Public
                                            </div>
                                        ) : (
                                            selectedSection?.shared_with?.length > 0 &&
                                            selectedSection?.created_by === user?.id && (
                                                <div className={styles.sectionTag} style={{ backgroundColor: '#192BC2' }}>
                                                    Shared
                                                </div>
                                            )
                                        )}
                                        {selectedSection?.is_shared && !selectedSection?.is_project_default && (
                                            <div className={styles.sectionTag} style={{ backgroundColor: '#192BC2' }}>
                                                {selectedSection?.owner_name}
                                            </div>
                                        )} */}
                                {!!selectedSection?.shared_with_names?.length &&
                                    selectedSection?.created_by === user?.id &&
                                    !selectedSection?.is_shared &&
                                    !selectedSection?.is_project_default && (
                                        <div className=" d-inline-flex align-items-center">
                                            <AvatarGroup layout="stack" size={28}>
                                                {selectedSection.shared_with_names.slice(0, 3).map(name => (
                                                    <AvatarGroupItem className={styles.avatarGroupItem} name={name} key={name} color="brand" />
                                                ))}
                                                {selectedSection.shared_with_names.length > 3 && (
                                                    <AvatarGroupPopover>
                                                        {selectedSection.shared_with_names.slice(3).map(name => (
                                                            <AvatarGroupItem className={styles.avatarGroupItem} name={name} key={name} color="brand" />
                                                        ))}
                                                    </AvatarGroupPopover>
                                                )}
                                            </AvatarGroup>
                                        </div>
                                    )}
                            </div>
                            <div
                                style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                            >
                                <ArrowSort24Regular
                                    className='me-2'
                                    color='var(--Brand-Secondary)'
                                    title={sortOrder === 'desc' ? 'Sorted: Newest first (click for oldest first)' : 'Sorted: Oldest first (click for newest first)'}
                                    onClick={() => setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc')}
                                    role="button"
                                />
                                <NoteAdd24Regular   title="Add new entry"  onClick={() => {
                                    if (!selectedSectionId) {
                                        toast.warning('Please select a section first');
                                        return;
                                    }
                                    // Clear any existing state
                                    setExpandedNoteId(null);
                                    setEditingNote(null);
                                    setInitialEditorData(null);
                                    setAutoSavedNoteId(null);
                                    isCreatingNoteRef.current = false;
                                    // Clear editor content
                                    if (editorApiRef.current?.clear) {
                                        editorApiRef.current.clear();
                                    }
                                    // Start adding new entry
                                    setIsAddingEntry(true);
                                }} color="var(--Brand-Secondary)" role="button" />
                            </div>
                            
                            {/* <div className="d-flex align-items-center gap-3">
                                {(expandedNoteId || isAddingEntry) && (
                                    // {expandedNoteId && !isAddingEntry && (
                                    <>
                                        <span
                                            className={styles.backToEntriesText}
                                            onClick={() => {
                                                if (isAddingEntry && editingNote) {
                                                    handleRewriteWithAI(editingNote);
                                                    return;
                                                }
                                                const expandedNote = selectedSection?.notes?.find(note => note.id === expandedNoteId);
                                                if (expandedNote) {
                                                    handleRewriteWithAI(expandedNote);
                                                }
                                            }}
                                            style={{ cursor: 'pointer' }}>
                                            Rewrite with AI
                                        </span>

                                        <Menu open={isMoveToSectionOpen} onOpenChange={(e, data) => setIsMoveToSectionOpen(data.open)}>
                                            <MenuTrigger disableButtonEnhancement>
                                                <span className={styles.backToEntriesText} style={{ cursor: 'pointer' }}>
                                                    Move to Section <ChevronDown16Regular />
                                                </span>
                                            </MenuTrigger>
                                            <MenuPopover>
                                                <MenuList>
                                                    {sections
                                                        .filter(section => section.id !== selectedSectionId)
                                                        .map(section => (
                                                            <MenuItem key={section.id} onClick={() => handleMoveToSection(section.id)}>
                                                                {section.name}
                                                            </MenuItem>
                                                        ))}
                                                </MenuList>
                                            </MenuPopover>
                                        </Menu>
                                        <Menu>
                                            <MenuTrigger disableButtonEnhancement>
                                                <span className={styles.backToEntriesText} style={{ cursor: 'pointer' }}>
                                                    Convert to <ChevronDown16Regular />
                                                </span>
                                            </MenuTrigger>
                                            <MenuPopover>
                                                <MenuList>
                                                    <MenuItem onClick={() => setShowObsSidebar(true)}>Convert to Observations</MenuItem>
                                                    <MenuItem onClick={() => setShowTaskSidebar(true)}>Convert to Tasks</MenuItem>
                                                    <MenuItem onClick={() => setShowIRLSidebar(true)}>Convert to IRL</MenuItem>
                                                </MenuList>
                                            </MenuPopover>
                                        </Menu>
                                    </>
                                )}
                                {(expandedNoteId || isAddingEntry) && (
                                    <span
                                        role="button"
                                        onClick={() => {
                                            // Always clear expanded note first
                                            if (expandedNoteId) {
                                                setExpandedNoteId(null);
                                            }
                                            // Then clear edit mode if active
                                            if (isAddingEntry) {
                                                setIsAddingEntry(false);
                                                setEditingNote(null);
                                                setInitialEditorData(null);

                                                setAutoSavedNoteId(null);
                                                isCreatingNoteRef.current = false;
                                                if (editorApiRef.current?.clear) {
                                                    editorApiRef.current.clear();
                                                }
                                            }
                                        }}
                                        className={styles.backToEntriesText}>
                                        Back to entries
                                    </span>
                                )}

                                {(!expandedNoteId || isAddingEntry) && (
                                    <button
                                        className={styles.addEntryButton}
                                        disabled={!selectedSectionId}
                                        onClick={async () => {
                                            if (!selectedSectionId) return;
                                            if (isAddingEntry) {
                                                // Force save before closing
                                                try {
                                                    await autoSave();
                                                } catch (error) {
                                                    console.error('Failed to save before closing:', error);
                                                }

                                                setIsAddingEntry(false);
                                                setEditingNote(null);
                                                setInitialEditorData(null);

                                                setAutoSavedNoteId(null);
                                                // Clear editor content
                                                if (editorApiRef.current?.clear) {
                                                    editorApiRef.current.clear();
                                                }
                                            } else {
                                                setIsAddingEntry(true);
                                                setAutoSavedNoteId(null);
                                                isCreatingNoteRef.current = false;
                                            }
                                        }}
                                        title={!selectedSectionId ? 'Select a section to add an entry' : isAddingEntry ? 'Save' : 'Add new entry'}>
                                        {!isAddingEntry && <Add20Regular color={!selectedSectionId ? 'var(--Neutral-Gray-6)' : 'white'} />}
                                        <div className={styles.addEntryText}>{isAddingEntry ? 'Save' : 'Add new entry'}</div>
                                    </button>
                                )}
                            </div> */}
                        </div>
                        {selectedSection?.is_project_default && (
                            <div className={styles.noticeBanner}>
                                <div className={styles.noticeBannerInner}>
                                    <div className="me-2">
                                        <Warning20Regular color="#f8047e" />
                                    </div>
                                    <span className={styles.noticePrefix}>
                                        Important Notice: Project Updates are visible to all project users. Share only information intended for the entire project audience.
                                    </span>{' '}
                                </div>
                            </div>
                        )}

                        {selectedSection && (
                            <div className={styles.entriesContainer}>
                                {sortedNotes.map(note => (
                                    <div
                                        key={note.id}
                                        className={styles.journalEntry}
                                        style={{
                                            backgroundColor: editingNote?.id === note.id ? 'rgba(142, 45, 226, 0.05)' : 'white',
                                        }}
                                        onClick={() => {
                                            setExpandedNoteId(null);
                                            setEditingNote(note);

                                            // Store original content to prevent saving unchanged content
                                            const noteHtml = getNoteHtml(note);
                                            originalNoteContentRef.current = noteHtml;

                                            // Set flag to prevent auto-save during initialization
                                            isInitializingEditorRef.current = true;
                                            setInitialEditorData(noteHtml);
                                            setIsAddingEntry(true);

                                            // Clear the flag after editor has time to initialize
                                            setTimeout(() => {
                                                isInitializingEditorRef.current = false;
                                            }, 2000);
                                        }}>
                                        <div className={styles.entryContent}>
                                            {expandedNoteId === note.id ? (
                                                <SafeHTML
                                                    html={note?.notes || ''}
                                                    className={styles.entryDescription}
                                                    tag="div"
                                                />
                                            ) : (
                                                <div className={styles.entryDescription}>
                                                    {(() => {
                                                        const html = note?.notes || '';
                                                        if (!html) return null;

                                                        // Parse HTML to get first two tags
                                                        const parser = new DOMParser();
                                                        const doc = parser.parseFromString(html, 'text/html');
                                                        const tags = doc.body.children;
                                                        const firstTwoTexts = [];

                                                        for (let i = 0; i < tags.length && i < 2; i++) {
                                                            firstTwoTexts.push(tags[i].textContent.trim());
                                                        }

                                                        if (firstTwoTexts.length === 0) {
                                                            // Fallback: extract plain text if no tags
                                                            const text = html.replace(/<[^>]+>/g, '');
                                                            const truncated = text.slice(0, 60);
                                                            return <p>{text.length > 60 ? truncated + '...' : truncated}</p>;
                                                        }

                                                        // First line: check if first tag is h2
                                                        const firstText = firstTwoTexts[0] || '';
                                                        const firstTruncated = firstText.length > 60 ? firstText.slice(0, 60) + '...' : firstText;

                                                        // Second line: second tag (if exists)
                                                        const secondText = firstTwoTexts[1] || '';
                                                        const secondTruncated = secondText.length > 60 ? secondText.slice(0, 60) + '...' : secondText;

                                                        return (
                                                            <>
                                                                {firstTruncated && (
                                                                    <p
                                                                        className="text-truncate"
                                                                        style={{ marginBottom: 4, fontSize: '16px', fontWeight: 'bold', maxWidth: '100%' }}>
                                                                        {firstTruncated}
                                                                    </p>
                                                                )}
                                                                {secondText && (
                                                                    <p className="text-truncate" style={{ margin: '4px 0 0 0', maxWidth: '100%' }}>
                                                                        {secondTruncated}
                                                                    </p>
                                                                )}
                                                            </>
                                                        );
                                                    })()}
                                                </div>
                                            )}
                                        </div>
                                        <div className={styles.entryFooterTop}>
                                            <div className="d-flex align-items-center gap-2">
                                                <div className={styles.customAvatar}>{getProjectMemberName(note.created_by)}</div>
                                                <div className={styles.entryDate}>{formatEntryDate(note.created)}</div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
                <div className={`col-sm-12 ${isSectionsCollapsed ? 'col-md-9 col-lg-9' : 'col-md-6 col-lg-6'}`}>
                    {/* White background when section is selected but has no notes */}
                    {!isAddingEntry && selectedSection && (!selectedSection?.notes || selectedSection.notes.length === 0) && (
                        <div
                            style={{
                                backgroundColor: 'white',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minHeight: '88vh',
                                marginTop: 23,
                                borderRadius: '4px',
                            }}>
                            <div
                                style={{
                                    textAlign: 'center',
                                    color: '#666',
                                    fontSize: '16px',
                                }}>
                                No notes in this section yet
                            </div>
                        </div>
                    )}

                    {/* Show message when section is selected but no entry is selected */}
                    {!isAddingEntry && selectedSection && selectedSection?.notes && selectedSection.notes.length > 0 && !editingNote && (
                        <div
                            style={{
                                backgroundColor: 'white',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minHeight: '88vh',
                                marginTop: 23,
                                borderRadius: '4px',
                            }}>
                            <div
                                style={{
                                    textAlign: 'center',
                                    color: '#666',
                                    fontSize: '16px',
                                }}>
                                Choose entry to display
                            </div>
                        </div>
                    )}

                    {isAddingEntry && (
                        <div className={styles.editorWrapper}>
                            <div className={styles.editorCard}>
                                <div className={styles.editorInner}>
                                    <div className={styles.editorBodyRow}>
                                        <JournalEditor
                                            initialData={initialEditorData}
                                            holder="editorjs"
                                            onReadyApi={api => (editorApiRef.current = api)}
                                            onChange={handleEditorChange}
                                            handleEntryDeleteClick={handleEntryDeleteClick}
                                            handleRewriteWithAI={mode => handleRewriteWithAI(mode, editingNote)}
                                            handleMoveEntry={() => {
                                                const currentNoteId = editingNote?.id || autoSavedNoteIdRef.current;
                                                console.log('handleMoveEntry called', { currentNoteId, editingNote, autoSavedNoteId: autoSavedNoteIdRef.current });
                                                if (currentNoteId) {
                                                    setIsMoveEntryModalOpen(true);
                                                } else {
                                                    toast.warning('Please save the note before moving it');
                                                }
                                            }}
                                            isNewNote={!editingNote && !initialEditorData}
                                            onBlur={async () => {
                                                // Clear any pending debounce timeout
                                                if (debounceTimeoutRef.current) {
                                                    clearTimeout(debounceTimeoutRef.current);
                                                    debounceTimeoutRef.current = null;
                                                }
                                                // Save immediately on blur
                                                if (isAddingEntryRef.current && selectedSectionIdRef.current) {
                                                    await autoSave();
                                                }
                                            }}
                                        />
                                        {/* <div className={styles.editorBodyPlaceholder}>Start writing...</div> */}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Share Modal */}
            <ShareSectionModal
                isOpen={isShareModalOpen}
                setIsShareModalOpen={setIsShareModalOpen}
                onOpenChange={(event, data) => setIsShareModalOpen(data.open)}
                sectionId={selectedSectionForShare?.id}
            />

            {/* Delete Section Confirmation Modal */}
            <DoubleConfirmationModal
                message={`Are you sure you want to delete the section "${selectedSectionForDelete?.name}"? This will also delete all entries in this section.`}
                handleAction={() => handleDeleteSection(selectedSectionForDelete?.id)}>
                <div ref={sectionMenuDeleteRef} style={{ display: 'none' }} />
            </DoubleConfirmationModal>

            {/* Make Private Confirmation Modal */}
            <DoubleConfirmationModal
                type={'warn '}
                modalOneBtnL="Make Private"
                modalTwoBtnR="Make Private"
                message={`Make ${selectedSectionForMakePrivate?.name} Private?\n\nThis section is currently shared with your team. Making it private will remove their access.`}
                handleAction={() => selectedSectionForMakePrivate && handleMakePrivate(selectedSectionForMakePrivate)}>
                <div ref={sectionMakePrivateRef} style={{ display: 'none' }} />
            </DoubleConfirmationModal>

            {/* Delete Entry Confirmation Modal */}
            <DoubleConfirmationModal
                message={`Are you sure you want to delete the entry "${selectedEntryForDelete?.title || 'Untitled'}"?`}
                handleAction={() => handleDeleteEntry(selectedEntryForDelete?.id)}>
                <div ref={entryMenuDeleteRef} style={{ display: 'none' }} />
            </DoubleConfirmationModal>

            {/* Rewrite AI Modal */}
            <RewriteAIModal
                isOpen={isRewriteAIModalOpen}
                onOpenChange={e => {
                    setIsRewriteAIModalOpen(e.open);
                    if (!e.open) {
                        // Clear rewritten content when modal closes
                        setRewrittenContentForSidebar(null);
                    }
                }}
                note={selectedNoteForRewrite}
                notepadId={selectedSectionId}
                setShowObsSidebar={rewrittenContent => {
                    if (rewrittenContent) {
                        setRewrittenContentForSidebar(rewrittenContent);
                    }
                    setShowObsSidebar(true);
                }}
                setShowTaskSidebar={rewrittenContent => {
                    if (rewrittenContent) {
                        setRewrittenContentForSidebar(rewrittenContent);
                    }
                    setShowTaskSidebar(true);
                }}
                setShowIRLSidebar={rewrittenContent => {
                    if (rewrittenContent) {
                        setRewrittenContentForSidebar(rewrittenContent);
                    }
                    setShowIRLSidebar(true);
                }}
                aiMode={aiMode}
                onReplace={newHtml => {
                    // If in edit mode, update the editor live with the rewritten content
                    if (isAddingEntry) {
                        setInitialEditorData(newHtml || '');
                        setDraftContent(newHtml || '');
                    }
                }}
            />

            {/* Move Entry Modal */}
            <MoveEntryModal
                isOpen={isMoveEntryModalOpen}
                onOpenChange={e => setIsMoveEntryModalOpen(e.open)}
                noteId={editingNote?.id || autoSavedNoteIdRef.current}
                sourceSectionId={selectedSectionId}
                sections={sections}
                onMoveSuccess={targetSectionId => {
                    // Reset edit/expanded state and switch to target section
                    setExpandedNoteId(null);
                    setIsAddingEntry(false);
                    setEditingNote(null);
                    setInitialEditorData(null);
                    setAutoSavedNoteId(null);
                    isCreatingNoteRef.current = false;
                    if (editorApiRef.current?.clear) {
                        editorApiRef.current.clear();
                    }
                    setSelectedSectionId(targetSectionId);
                }}
            />

            <ObservationFormSidebar
                show={showObsSidebar}
                setShow={show => {
                    setShowObsSidebar(show);
                    if (!show) {
                        // Clear rewritten content when sidebar closes
                        setRewrittenContentForSidebar(null);
                    }
                }}
                defaultObservation={() => {
                    // Use rewritten content if available, otherwise use original note content
                    if (rewrittenContentForSidebar) {
                        return rewrittenContentForSidebar;
                    }
                    const currentNote = isAddingEntry && editingNote ? editingNote : selectedSection?.notes?.find(note => note.id === expandedNoteId);
                    return currentNote?.notes || '';
                }}
            />

            <TaskFormSidebar
                show={showTaskSidebar}
                setShow={show => {
                    setShowTaskSidebar(show);
                    if (!show) {
                        // Clear rewritten content when sidebar closes
                        setRewrittenContentForSidebar(null);
                    }
                }}
                defaultTask={() => {
                    // Use rewritten content if available, otherwise use original note content
                    if (rewrittenContentForSidebar) {
                        return htmlToPlainText(rewrittenContentForSidebar);
                    }
                    // Use editingNote if in edit mode, otherwise find expanded note
                    const currentNote = isAddingEntry && editingNote ? editingNote : selectedSection?.notes?.find(note => note.id === expandedNoteId);
                    return htmlToPlainText(currentNote?.notes || '');
                }}
            />
            <PostInformationRequestSidebar
                show={showIRLSidebar}
                setShow={show => {
                    setShowIRLSidebar(show);
                    if (!show) {
                        // Clear rewritten content when sidebar closes
                        setRewrittenContentForSidebar(null);
                    }
                }}
                defaultRequest={() => {
                    // Use rewritten content if available, otherwise use original note content
                    if (rewrittenContentForSidebar) {
                        return rewrittenContentForSidebar;
                    }
                    const currentNote = isAddingEntry && editingNote ? editingNote : selectedSection?.notes?.find(note => note.id === expandedNoteId);
                    return currentNote?.notes || '';
                }}
            />
        </div>
    );
};
export default Journal;
